import React, { useCallback, useMemo } from 'react';
import { styled } from 'styled-components';
import { COLORS } from '../../../styles/style';
import { Divider } from 'antd';
import { Column, IconButton, IconButton_IconSizes, OverlayMessage, RowCentered } from '@ydistri/ds';
import { AvailableModals, openModal } from '../../../screens/Modals/modalSlice';
import { MdLockOutline, MdOpenInNew } from 'react-icons/md';
import PotentialValues from './PotentialValues';
import {
  CalculationMode,
  CalculationStatus,
  CalculationWithOverviewResponse,
} from '@ydistri/api-sdk';
import OverlapIndicator from '../../../screens/Calculations/components/row/OverlapIndicator';
import { emptyCalculationsWithOverviewResponse } from '../../../screens/Calculations/components/row/calculationRowLib';
import { useAppDispatch } from '../../../store';
import { Overlay } from '../Overlay';

const PotentialOverviewWrapper = styled(RowCentered)<{ $isOutdated: boolean }>`
  flex-wrap: nowrap;
  font-size: 1.5rem;
  color: ${COLORS.ALL_SKU_TYPES_COLOR};
  background-color: ${p => (p.$isOutdated ? COLORS.GREY_LIGHT : COLORS.PRIMARY_STEPS[0])};
  border-radius: 0.5rem;
  gap: 0.5rem;
  padding: 0.25rem 1rem;

  .ant-statistic-title {
    font-size: 0.8rem;
    margin-bottom: 0;
  }

  .ant-statistic-content {
    font-size: 1rem;
  }
`;

const IconsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  transition: 0.3s all;
  cursor: pointer;
  gap: 0.5rem;
`;

const openModalIcon = <MdOpenInNew size={IconButton_IconSizes.small} />;

function getDetailedStatisticsTooltip(correctMode: boolean, correctStatus: boolean): string {
  if (!correctMode) {
    return 'Detailed statistics for merged and manual calculations are not present';
  }
  if (!correctStatus) {
    return 'Calculation was edited in the meantime, please recompute statistics.';
  }
  return 'Open detailed statistics';
}

interface CalculationPotentialOverviewProps {
  calculationMode: CalculationMode;
  calculationStatus: CalculationStatus;
  overlappingCalculations?: CalculationWithOverviewResponse[] | null;
}

const CalculationPotentialOverview: React.FC<CalculationPotentialOverviewProps> = ({
  calculationMode,
  calculationStatus,
  overlappingCalculations = emptyCalculationsWithOverviewResponse,
}: CalculationPotentialOverviewProps) => {
  const dispatch = useAppDispatch();

  const openCategoryInsightsModal = useCallback(() => {
    dispatch(openModal({ type: AvailableModals.CATEGORY_INSIGHTS, data: undefined }));
  }, [dispatch]);

  const correctMode = calculationMode === CalculationMode.Computed;
  const correctStatus = calculationStatus !== CalculationStatus.Editing;
  const disableStatisticsModalButton = !correctMode || !correctStatus;
  const detailedStatisticsTooltip = getDetailedStatisticsTooltip(correctMode, correctStatus);

  const potentialValuesOverlayMessage = useMemo(
    () => (
      <OverlayMessage>
        <MdLockOutline />{' '}
        <Column>
          <span>Redistribution was changed</span>
          <span style={{ fontSize: '0.85rem' }}>Please recompute statistics again</span>
        </Column>
      </OverlayMessage>
    ),
    [],
  );

  return (
    <PotentialOverviewWrapper $isOutdated={false}>
      {overlappingCalculations && overlappingCalculations.length > 0 && (
        <>
          <OverlapIndicator overlappingCalculations={overlappingCalculations} />
          <Divider type="vertical" />
        </>
      )}
      <Overlay
        active={!correctStatus}
        overlayContent={potentialValuesOverlayMessage}
        minWidth="25rem"
      >
        <PotentialValues calculationMode={calculationMode} />
      </Overlay>
      <Divider type="vertical" />
      <IconsWrapper>
        <IconButton
          size="small"
          icon={openModalIcon}
          onClick={openCategoryInsightsModal}
          tooltip={detailedStatisticsTooltip}
          tooltipPosition="left"
          disabled={disableStatisticsModalButton}
        />
      </IconsWrapper>
    </PotentialOverviewWrapper>
  );
};

export default CalculationPotentialOverview;
